import { useMemo } from 'react';
import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';
import { getCountryCodeValueList } from '@utils/country-list/country-list';
import { useGetCountriesQuery, type GetCountriesQuery } from './get-countries.queries.generated';

type Country = GetCountriesQuery['countries'][0];
type CountryEntry = Exclude<Country, null>;

const hasValidId = (country?: Record<string, unknown> | null): country is CountryEntry => {
    return !!country?.id;
};
export type PhoneCountryCode = Pick<
    CountryEntry,
    'id' | 'openingHours' | 'phoneNumber' | 'email'
> & {
    label: string;
    /** The flag and calling code alone, "🇸🇪 +46", for where the country's name doesn't fit. */
    shortLabel: string;
    value: string;
    country: CountryCode;
};

const isSupportedCountryCode = (value?: string): value is CountryCode => {
    if (!value) return false;
    try {
        getCountryCallingCode(value as CountryCode);
        return true;
    } catch {
        return false;
    }
};

const toCountryCodes = (countries: CountryEntry[]): CountryCode[] =>
    countries.map((country) => country.id.toUpperCase()).filter(isSupportedCountryCode);

export const useSupportedCountries = () => {
    const { data, loading, error } = useGetCountriesQuery();

    const countries = useMemo(() => (data?.countries ?? []).filter(hasValidId), [data?.countries]);

    const countryCodes = useMemo(() => toCountryCodes(countries), [countries]);

    return { countries, countryCodes, loading, error };
};

export const usePhoneCountryCodes = () => {
    const { countryCodes, countries } = useSupportedCountries();

    const phoneCountryCodes = useMemo<PhoneCountryCode[]>(() => {
        if (!countryCodes.length) return [];

        const countriesById = new Map(
            countries.map((entry) => [entry.id.toUpperCase(), entry] as const)
        );

        return getCountryCodeValueList(countryCodes).map(
            ({ flag, country, callingCode, countryCode }) => {
                const name = country ?? countryCode;
                const labelPrefix = flag ? `${flag} ` : '';
                const match = countriesById.get(countryCode);

                return {
                    id: match?.id ?? countryCode,
                    openingHours: match?.openingHours ?? null,
                    phoneNumber: match?.phoneNumber ?? null,
                    email: match?.email ?? null,
                    label: `${labelPrefix}${name} ${callingCode}`,
                    shortLabel: `${labelPrefix}${callingCode}`,
                    value: callingCode.startsWith('+') ? callingCode.substring(1) : callingCode,
                    country: countryCode,
                };
            }
        );
    }, [countryCodes, countries]);

    return { phoneCountryCodes };
};
